/* eslint-disable react-hooks/exhaustive-deps */
import { FEATURE_KEYS } from "../../access-control/models/role-feature.models";
import { useFeatureAccess } from "../../access-control/hooks/useFeatureAccess";
import { FileDollarIcon } from "../../shared/Icons";
import Header from "../../shared/app-shell/Header";
import PageFooter from "../../shared/app-shell/PageFooter";
import Sidebar from "../../shared/app-shell/Sidebar";
import { featureFlagsAtom, themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { hasVoyagePnlSummary } from "../../voyage-pnl/services/pnl.service";
import { CargoDetailsFactory } from "../components/CargoDetails";
import OpsKnowledgeAssistant from "../components/OpsKnowledgeAssistant";
import UWHCAssistant from "../components/UWHCAssistant";
import { VoyageCargo } from "../components/VoyageCargo";
import { VoyageDetails } from "../components/VoyageDetails";
import VoyageDetailsChecklistTab from "../components/VoyageDetailsChecklistTab";
import { VoyageItinerary } from "../components/VoyageItinerary";
import VoyageNotification from "../components/VoyageNotification";
import type {
  ItinaryAndCargo,
  VoyageCargoM,
  VoyageInfo,
  VoyageItineraryM,
  VoyageStatus,
} from "../models/voyage.interfaces";
import {
  getRealtimeInfo,
  getVoyageCargoDetails,
  getVoyageItinerary,
  getVoyageSequenceItinerary,
} from "../services/voyages.service";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const VoyageDetailsPage = () => {
  const theme = useAtomValue(themeAtom);
  const navigate = useNavigate();
  const { hasAnyFeature } = useFeatureAccess();

  const featureFlags = useAtomValue(featureFlagsAtom);
  const canAccessPnl = hasAnyFeature([FEATURE_KEYS.VOYAGE_PNL, FEATURE_KEYS.REVIEW_PNL]);

  const { tcCode, voyageNo } = useParams();
  const checklistKey = `${tcCode ?? ""}-${voyageNo ?? ""}`;
  const [isLoading, setIsLoading] = useState(false);
  const [voyageItinerary, setVoyageItinerary] = useState<ItinaryAndCargo | null>(null);
  const [voyageInfo, setVoyageInfo] = useState<VoyageInfo[]>([]);
  const [realtimeStatus, setRealtimeStatus] = useState<VoyageStatus | null>(null);
  const [cargo, setCargo] = useState<VoyageCargoM[]>([]);
  const [isPnlAvailable, setIsPnlAvailable] = useState(false);
  const [isPnlAvailabilityLoading, setIsPnlAvailabilityLoading] = useState(false);

  useEffect(() => {
    if (tcCode && voyageNo) {
      fetchVoyageSequenceItinerary();
      fetchVoyageItinerary();
      fetchVoyageCargoDetails();
      fetchRealtimeInfo();
    }
  }, [tcCode, voyageNo]);

  useEffect(() => {
    let isActive = true;

    if (!tcCode || !voyageNo || !canAccessPnl) {
      setIsPnlAvailable(false);
      setIsPnlAvailabilityLoading(false);
      return () => {
        isActive = false;
      };
    }

    setIsPnlAvailabilityLoading(true);
    setIsPnlAvailable(false);

    const checkPnlAvailability = async () => {
      const isAvailable = await hasVoyagePnlSummary(tcCode, voyageNo);
      if (!isActive) return;

      setIsPnlAvailable(isAvailable);
      setIsPnlAvailabilityLoading(false);
    };

    checkPnlAvailability();

    return () => {
      isActive = false;
    };
  }, [tcCode, voyageNo, canAccessPnl]);

  const fetchVoyageSequenceItinerary = async () => {
    try {
      setIsLoading(true);
      const voyageInfo = await getVoyageSequenceItinerary(tcCode, voyageNo);
      setVoyageInfo(voyageInfo);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVoyageItinerary = async () => {
    try {
      setIsLoading(true);
      const voyageItinerary = await getVoyageItinerary(tcCode, voyageNo);
      setVoyageItinerary(voyageItinerary);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRealtimeInfo = async () => {
    try {
      setIsLoading(true);
      const realtimeStatus = await getRealtimeInfo(tcCode, voyageNo);
      setRealtimeStatus(realtimeStatus);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVoyageCargoDetails = async () => {
    try {
      setIsLoading(true);
      const cargo = await getVoyageCargoDetails(tcCode, voyageNo);
      const filteredCargo = cargo?.filter((item: { CARGOID_TO_CONSIDER: string }) => item.CARGOID_TO_CONSIDER === "Y");
      setCargo(filteredCargo);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  // Failsafe
  let itinerary: VoyageItineraryM[] = [];
  const itineraryIsArr = Array.isArray(voyageItinerary?.voyageItinerary);
  if (itineraryIsArr) {
    itinerary = voyageItinerary?.voyageItinerary.sort((a, b) => a.order - b.order) || [];
  } else {
    console.warn("voyageItinerary?.voyageItinerary should be an array. Check the response, might have an error");
    itinerary = [];
  }

  const [expandedIndexes, setExpandedIndexes] = useState<boolean[]>(new Array(itinerary?.length ?? 0).fill(false));

  const toggleExpand = (index: number) => {
    const updatedIndexes = [...expandedIndexes];
    updatedIndexes[index] = !updatedIndexes[index];
    setExpandedIndexes(updatedIndexes);
  };

  const goToIMSBClink = () => window.open(`https://www.imorules.com/IMSBC_APP1.html`, "_blank");

  const CargoDetails = CargoDetailsFactory(voyageItinerary);
  const isChatEnabled = featureFlags.chat_enabled;
  const firstActivePort = itinerary.find((item) => item.IS_DEPARTED !== "Y")?.VI_PORT_NAME?.trim();
  const fallbackLoadPort = voyageInfo?.[0]?.LOAD_PORT?.trim();
  const fallbackDischargePort = voyageInfo?.[0]?.DISCHARGE_PORT?.trim();
  const opsPortName = firstActivePort || fallbackLoadPort || fallbackDischargePort || null;
  const currentCommodity =
    Array.from(
      new Set(cargo.map((item) => item.CARGO?.trim()).filter((value): value is string => Boolean(value))),
    ).join(", ") || null;
  const vesselName = voyageInfo?.[0]?.VESSEL_NAME?.trim() || cargo[0]?.VESSEL_NAME?.trim() || null;
  const canOpenPnl =
    !!tcCode && !!voyageNo && isPnlAvailable && !isPnlAvailabilityLoading && canAccessPnl;

  const handleOpenPnl = () => {
    if (!canOpenPnl) return;
    navigate(`/voyage/details/${tcCode}/${voyageNo}/pnl`);
  };

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg" zIndex={20}>
      <div className="flex h-screen">
        <Sidebar />

        <div className={twMerge("flex flex-1 flex-col gap-8 overflow-y-auto px-6", theme.home.tailwind.pageBackground)}>
          <Header />

          <div>
            <div className="flex items-center justify-between gap-6 px-6">
              <span className={twMerge("text-2xl font-semibold", theme.home.tailwind.textDefault)}>
                Voyage #{voyageNo}
              </span>
              <button
                type="button"
                onClick={handleOpenPnl}
                disabled={!canOpenPnl}
                className={twMerge(
                  "inline-flex items-center gap-2 rounded border px-3 py-2 text-sm font-medium transition-colors focus:outline-none",
                  theme.headerComponent.tailwind.buttonBorder,
                  theme.headerComponent.tailwind.backButtonBg,
                  theme.myVesselsTable.tailwind.linkText,
                  canOpenPnl
                    ? twMerge(
                        theme.headerComponent.tailwind.buttonHoverBg,
                        theme.headerComponent.tailwind.buttonHoverText,
                      )
                    : "cursor-not-allowed",
                )}
              >
                <FileDollarIcon className="h-[18px] w-[18px]" aria-hidden="true" />
                <span>PnL</span>
              </button>
            </div>

            <div className="pt-8">
              <VoyageNotification />

              <div className="flex flex-row items-start gap-6">
                <div className="flex w-[17%] flex-none flex-col gap-6">
                  <VoyageDetails voyageInfo={voyageInfo} />
                  <VoyageCargo cargo={cargo} gotoLink={goToIMSBClink} />
                </div>
                <div className="flex w-[40%] min-w-0 flex-none flex-col gap-6">
                  <VoyageItinerary
                    itinerary={itinerary}
                    expandedIndexes={expandedIndexes}
                    toggleExpand={toggleExpand}
                    renderCargoDetails={CargoDetails}
                  />
                  {isChatEnabled ? (
                    <VoyageDetailsChecklistTab key={checklistKey} realtimeStatus={realtimeStatus} />
                  ) : null}
                </div>
                <div className="flex w-[40%] min-w-0 flex-none flex-col gap-6">
                  {isChatEnabled ? (
                    <>
                      <OpsKnowledgeAssistant
                        portName={opsPortName}
                        vesselName={vesselName}
                        currentCommodity={currentCommodity}
                      />
                      <UWHCAssistant />
                    </>
                  ) : (
                    <VoyageDetailsChecklistTab key={checklistKey} realtimeStatus={realtimeStatus} />
                  )}
                </div>
              </div>
            </div>

            <PageFooter />
          </div>
        </div>
      </div>
    </LoadingWrapper>
  );
};

export default VoyageDetailsPage;
